// Third-party
import React from 'react';
import moment from 'moment';
import { isEqual } from 'lodash';

// Components
import { HoldingIcon } from '../../common';

// Utils
import { enumerateArray, enumerateJSX, uniqueArray, truncateString } from '../../../../utils/objects';
import { formatMessageWithLocale } from '../../../../utils/i18n';
import { COMPARISON_ITEM_TYPES } from '../../../../utils/constants';
import Dictionary from '@/types/dictionary';
import ComparisonItemType from '@/types/comparison-item-type';
import ComparisonHolding from '@/types/comparison-holding';
import Locale from '@/types/locale';

type DetailedDisclaimerData = {
  id: string;
  name: string;
  color?: string;
  dates: {
    date: Date;
    symbol?: string;
  }[];
  type: ComparisonItemType;
  holdings: ComparisonHolding[];
};

interface Props {
  globalPrefix: string;
  itemPrefix: string;
  itemDates: DetailedDisclaimerData[];
  showItemNames?: boolean;
  locale?: Locale;
}

class DetailedDatesDisclaimer extends React.Component<Props> {
  shouldComponentUpdate(nextProps: Props) {
    return !isEqual(this.props, nextProps);
  }

  formatDates = (dates: { date: Date; symbol?: string }[]) => {
    const { locale } = this.props;

    const map: Dictionary<Array<string | undefined>> = {};

    for (const d of dates) {
      const formatted = moment.utc(d.date).format(formatMessageWithLocale('Common.DateFormats.Short', locale));
      map[formatted] = map[formatted] || [];
      map[formatted].push(d.symbol);
    }

    const formattedDates: string[] = [];
    for (const [key, value] of Object.entries(map)) {
      formattedDates.push(`${key} (${value.filter(v => !!v).join(', ')})`);
    }

    return enumerateArray(uniqueArray(formattedDates), formatMessageWithLocale('Common.And', locale));
  };

  getHoldingIcon = (type: ComparisonItemType, color?: string) => (!color ? null : <HoldingIcon type={type} color={color} />);

  getNameElement = (item: DetailedDisclaimerData) => {
    let label = truncateString(item.name, 40);

    if (item.type !== COMPARISON_ITEM_TYPES.PORTFOLIO && item.type !== COMPARISON_ITEM_TYPES.GROUP && item.holdings[0].symbol) {
      label = item.holdings[0].symbol;
    }

    return (
      <span key={item.id} className="item-name" data-private data-dd-privacy="mask">
        {this.getHoldingIcon(item.type, item.color)}
        {label}
      </span>
    );
  };

  render() {
    const { itemDates, globalPrefix, itemPrefix, showItemNames, locale } = this.props;

    const localShowItemNames: boolean = showItemNames === undefined ? true : showItemNames;

    return (
      <div className="dates-disclaimer disclaimer">
        {globalPrefix}

        {enumerateJSX(
          itemDates.map(itemDate => (
            <div className="disclaimer-item" key={itemDate.dates.join()}>
              <span>{` ${itemPrefix} ${this.formatDates(itemDate.dates)}`}</span>

              {localShowItemNames && (
                <>
                  {` ${formatMessageWithLocale('Common.For', locale)} `}
                  {this.getNameElement(itemDate)}
                </>
              )}
            </div>
          )),
          formatMessageWithLocale('Common.And', locale),
        )}

        <span>. </span>
      </div>
    );
  }
}

export default DetailedDatesDisclaimer;
